/**
 * My Day's "reports not recorded": the practitioner's own sessions of the
 * last 30 days, before today, that can still be completed — a booking whose
 * time has passed and was never recorded (or was started and left open), and
 * a group occurrence they lead whose shared report is not written. Cancelled
 * and no-show bookings are not work left undone, and today's own rows are
 * already on the day above. A delivered session is sealed and always carries
 * its report (Product 2026-10-01), so it never appears here.
 */

import type { BookingView } from '@/core/clinic/bookingsApi';
import type { GroupSessionView } from '@/core/clinic/groupSessionsApi';
import { isParticipation } from '../scheduling/bookingRules';

export const UNRECORDED_DAYS = 30;

export type UnrecordedItem =
  | { kind: 'booking'; id: string; startsAt: string; booking: BookingView }
  | { kind: 'group'; id: string; startsAt: string; group: GroupSessionView };

export function unrecordedItems(
  bookings: readonly BookingView[],
  groups: readonly GroupSessionView[],
  opts: { myUid: string; before: Date },
): UnrecordedItem[] {
  const before = opts.before.getTime();
  const past = (iso: string) => Date.parse(iso) < before;
  const out: UnrecordedItem[] = [];
  for (const b of bookings) {
    if (isParticipation(b) || b.practitionerUid !== opts.myUid || !past(b.startsAt)) continue;
    if (b.status !== 'booked') continue;
    if (b.executionStatus !== 'scheduled' && b.executionStatus !== 'in_progress') continue;
    out.push({ kind: 'booking', id: b.id, startsAt: b.startsAt, booking: b });
  }
  for (const g of groups) {
    if (g.practitionerUid !== opts.myUid || !past(g.startsAt)) continue;
    if (g.status !== 'scheduled' || g.groupReport !== null) continue;
    out.push({ kind: 'group', id: g.id, startsAt: g.startsAt, group: g });
  }
  // Most recent first — the session the practitioner still remembers best.
  return out.sort((x, y) => Date.parse(y.startsAt) - Date.parse(x.startsAt));
}
